<template>
  <div class="wre-dashboard">
    <el-container class="wre-dashboard-el-container">
      <el-aside width="270px">

        <span
          class="wre-dashboard-logo"
        />

        <div style="text-align: center;">
          <el-button
            @click="onShowSubAccountsTable"
            icon="i-workstations-o--16"
            class="wre-dashboard-sub-account-button"
            native-type="button"
            :disabled="isCheckSubAccountInProgress || isUserNotHaveAnySubAccounts"
            :loading="isCheckSubAccountInProgress"
            type="translucent">
            <span v-if="isActiveVolumeSubscription">
              {{ $t('wre-dashboard.sub-accounts-dialog-box.label.volume-subscription') }}
            </span>
            <span v-else>
              &nbsp;{{ getActiveSubAccountDisplayName }}
            </span>
          </el-button>
        </div>

        <nav-menu
          class="wre-dashboard-nav-menu"
          @navMenuItemSelected="handleNavMenuItemSelected"
        />

        <space-indicator
          class="wre-dashboard-space-indicator"
          :used="getStorageSpace.used"
          :total="getStorageSpace.total"
          :indicator-type="getStorageSpace.indicatorType"
        />

      </el-aside>
      <el-container class="wre-dashboard-el-container">
        <el-header :bordered="false">
          <span
            slot="aside"
            class="centered wre-el-header-container"
          >

            <el-row class="wre-dashboard-el-header">
              <el-col
                :span="16"
                class="wre-dashboard-el-header"
              >

                <span
                  class="el-text el-text--display-medium"
                  v-if="!isBreadCrumbsVisible">
                  {{ activeMenuItem.name }}
                </span>

                <content-table-breadcrumbs
                  :menu-item-id="this.activeMenuItem.id"
                  :menu-item-display-name="this.activeMenuItem.name"
                  :sub-account-id="this.selectedMachine.subAccountId"
                  :machine-display-name="this.selectedMachine.displayName"
                  :archive-id="this.selectedBox.id"
                  :archive-display-name="this.selectedBox.displayName"
                  :is-visible="isBreadCrumbsVisible"
                  @breadcrumbItemClick="handleBreadcrumbItemClick"
                />

                &nbsp;
              </el-col>

              <el-col
                :span="6"
                class="wre-dashboard-el-header"
                style="text-align: right; padding: 0;">
                <el-input
                  v-if="isSearchInputVisible"
                  v-model="searchInputValue"
                  class="mx-16 wre-dashboard-el-header-search-input"
                  value=""
                  :placeholder="$t('wre-dashboard.search.input.hint')"
                  suffix-icon="i-search-o--16"
                  size="small"
                  @change="onSearchInputChange"
                  :on-icon-click="onSearchInputIconClick"
                  :disabled="isTableDataLoading"
                  autofocus
                />
              </el-col>


              <el-col
                :span="1"
                class="wre-dashboard-el-header"
                style="text-align: right; padding: 0;">
                <el-button
                  v-if="getSearchEnabled && !isSearchInputVisible"
                  class="wre-dashboard-el-header-button"
                  type="text"
                  @click="searchButtonClick">
                  <el-icon
                    color="brand-primary"
                    name="i-search-o--24"
                  />
                </el-button>
              </el-col>

              <el-col
                :span="1"
                class="wre-dashboard-el-header"
                style="text-align: right; padding: 0;">
                <el-button
                  type="text"
                  class="wre-dashboard-el-header-button"
                  @click="helpButtonClick">
                  <el-icon
                    color="brand-primary"
                    name="i-question-circle-o--24"
                  />
                </el-button>
              </el-col>

            </el-row>

          </span>
        </el-header>

        <el-main class="el-main-container">

          <boxes-table
            v-if="isActiveBoxTable"
            :box-list="getSubAccountArchives"
            @boxSelected="handleBoxSelected"
          />

          <machines-table
            v-if="isActiveMachinesTable"
            :machine-list="getVolumeSbMachines"
            @machineSelected="handleMachineSelected"
          />

          <content-table
            v-if="isActiveContentTable"
            :content-list="getContentItems"
            :archive-id="selectedBox.id"
            :box-type="selectedBox.type"
            :app-features="getAppFeatures"
            :status-column-hidden="statusColumnHidden"
            :path-column-hidden="!getSearchResultReceived"
            :search-value="searchInputValue"
            @folderSelected="loadContent"
          />

          <shared-content-table
            v-if="isActiveSharedContentTable"
          />

          <account-page
            v-if="isActiveAccountPage"
            :used="getStorageSpace.used"
            :total="getStorageSpace.total"
            :indicator-type="getStorageSpace.indicatorType"
            :user-name="getUserSubscription.userName"
            :subscription-type="getUserSubscription.subscriptionType"
            :expiration-date="getUserSubscription.expirationDate"
            :brand-name="getBrandName"
          />

        </el-main>

      </el-container>
    </el-container>

    <password-dialog-box
      v-if="passwordDialogBoxVisible"
      :archive-id="passwordDialogBoxBox.id"
      :box-group="passwordDialogBoxBox.group"
      :archive-display-name="passwordDialogBoxBox.displayName"
      @handleSubmit="handlePasswordSubmit"
      @handleClose="handlePasswordClose"
    />

    <sub-accounts-table
      v-if="isSubAccountsTableVisible"
      @handleClose="handSubAccountsTableClose"
    />

    <router-view />
  </div>
</template>

<style lang="scss" scoped>
  $wre-sp-indicator-block-height: 160px;
  $wre-default-elements-padding: 16px;
  $wre-sub-account-button-height: 64px;

  .wre-dashboard-el-header {
    height: 64px;
    line-height: 64px;
  }

  .wre-dashboard-sub-account-button {
    margin-top: $wre-default-elements-padding;
    margin-bottom: $wre-default-elements-padding;
    height: 32px;
    line-height: 32px;
    width: 238px;
  }

  .wre-dashboard-el-header-search-input {
    text-align: left;
    max-width: 267px;
    line-height: 32px;
    height: 32px;
    margin-top: 19px;
  }

  .wre-dashboard-el-header-button {
    line-height: 24px;
    height: 24px;
    width: 24px;
  }

  .wre-dashboard-logo {
    display: inline-block;
    text-align: left;
    background: url('../assets/logo/wre-logo-white.svg') no-repeat;
    width: 256px;
    height: 64px;
  }

  .wre-dashboard-el-container {
    height: 100vh;
  }

  .el-header, .el-footer, .el-main {
    background-color: $av-fixed-white;
  }

  .wre-dashboard-nav-menu {
    height: calc(100vh - #{$wre-sp-indicator-block-height} - #{$wre-sub-account-button-height});
  }

  .wre-dashboard-space-indicator {
    padding: $wre-default-elements-padding;
    height: $wre-sp-indicator-block-height;
  }

  .el-header {
    position: relative;
    /*background-color: $av-brand-lighter;*/
    line-height: calc(#{$wre-default-elements-padding} * 4);
  }

  .wre-el-header-container {
    padding-left: $wre-default-elements-padding;
    padding-right: $wre-default-elements-padding;
  }

  /*
  .el-main-container {
    padding: $wre-default-elements-padding;
  }
  */

  .el-aside {
    background-color: $av-nav-primary;
    color: $av-fixed-primary;
  }

  .centered {
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
  }
</style>

<script>
import { createNamespacedHelpers } from 'vuex';

import NavMenu from './NavMenu';
import SpaceIndicator from './SpaceIndicator';
import BoxesTable from './BoxesTable';
import MachinesTable from './MachinesTable';
import PasswordDialogBox from './PasswordDialogBox';
import ContentTable from './ContentTable';
import SharedContentTable from './SharedContentTable';
import AccountPage from './AccountPage';
import ContentTableBreadcrumbs from './ContentTableBreadcrumbs';
import SubAccountsTable from './SubAccountsTable';

import {
  PENDING_STATUS
} from '../store/action-status';

import {
  FETCH_NAV_MENU_ITEMS,
  FETCH_STORAGE_SPACE,
  FETCH_CONTENT_ITEMS,
  FETCH_SUB_ACCOUNT_ITEMS,
  FETCH_SUB_ACCOUNT_ARCHIVES,
  SEARCH_CONTENT_ITEMS,
  UPDATE_ACTIVE_CONTENT_TYPE,
  UPDATE_ACTIVE_NAV_MENU_ITEM_ID,
  CHECK_SUB_ACCOUNT_EXISTS,
  UPDATE_ACTIVE_VOLUME_SB_SUB_ACCOUNT_ID
} from '../store/action-types';

import {
  BACKUP_GROUP_TYPE,
  MACHINES_TABLE_TYPE,
  CONTENT_TABLE_TYPE,
  SHARED_CONTENT_TABLE_TYPE,
  ACCOUNT_PAGE_CONTENT_TYPE
} from '../store/active-content-type';

import backupType from '../store/backup-types';

const { mapState, mapGetters, mapActions } = createNamespacedHelpers('wre-dashboard');

export default {
  name: 'WreDashboard',
  components: {
    NavMenu,
    SpaceIndicator,
    BoxesTable,
    MachinesTable,
    PasswordDialogBox,
    ContentTable,
    SharedContentTable,
    AccountPage,
    ContentTableBreadcrumbs,
    SubAccountsTable
  },
  data: function () {
    return {
      selectedBox: { id: '', type: 'BACKUP', displayName: '' },
      selectedMachine: { subAccountId: 0, displayName: '' },
      fullScreenLoader: '',
      isFullScreenLoaderActive: false,
      passwordDialogBoxVisible: false,
      passwordDialogBoxBox: '',
      isShowSharedContent: false,
      activeMenuItem: { id: '', name: '' },
      statusColumnHidden: true,
      isSearchInputVisible: false,
      isSubAccountsTableVisible: false,
      searchInputValue: ''
    };
  },
  computed: {
    ...mapState([
      'navMenuItems',
      'storageSpace'
    ]),
    ...mapGetters([
      'getSubAccountArchives',
      'getContentItems',
      'getStorageSpace',
      'getFetchSubAccountArchivesStatus',
      'getFetchContentItemsStatus',
      'getPostLoginOpenArchiveId',
      'getPostLoginOpenNavMenuId',
      'getActiveContentType',
      'getFetchSharedContentItemsStatus',
      'getUserSubscription',
      'getAppFeatures',
      'getSearchEnabled',
      'getBrandName',
      'getSearchResultReceived',
      'getActiveVolumePath',
      'getActiveFolderItem',
      'getActiveVolumeSubscription',
      'getActiveSubAccountDisplayName',
      'getActiveSubAccountId',
      'getFetchSubAccountItemsStatus',
      'getCheckSubAccountStatus',
      'getSubAccountItems',
      'getVolumeSbMachines',
      'getActiveNavMenuItemId'
    ]),
    isTableDataLoading() {
      return this.getFetchContentItemsStatus === PENDING_STATUS;
    },
    isActiveBoxTable() {
      return this.getActiveContentType === BACKUP_GROUP_TYPE;
    },
    isActiveMachinesTable() {
      return this.getActiveContentType === MACHINES_TABLE_TYPE;
    },
    isBreadCrumbsVisible() {
      if (this.getActiveVolumeSubscription === true) {
        return this.getActiveContentType === BACKUP_GROUP_TYPE
          || this.getActiveContentType === CONTENT_TABLE_TYPE;
      }
      return this.getActiveContentType === CONTENT_TABLE_TYPE;
    },
    isActiveContentTable() {
      return this.getActiveContentType === CONTENT_TABLE_TYPE;
    },
    isActiveSharedContentTable() {
      return this.getActiveContentType === SHARED_CONTENT_TABLE_TYPE;
    },
    isActiveAccountPage() {
      return this.getActiveContentType === ACCOUNT_PAGE_CONTENT_TYPE;
    },
    isCheckSubAccountInProgress() {
      return this.getCheckSubAccountStatus === PENDING_STATUS;
    },
    isFetchSubAccountItemsInProgress() {
      return this.getFetchSubAccountItemsStatus === PENDING_STATUS;
    },
    isUserNotHaveAnySubAccounts() {
      return !(this.getSubAccountItems.length > 0);
    },
    isActiveVolumeSubscription() {
      return this.getActiveVolumeSubscription === true;
    }
  },
  watch: {
    getActiveSubAccountId(subAccountId) {
      const isVolumeSubscription = subAccountId < 0;
      if (this.getActiveNavMenuItemId === 'BACKUPS' ||
        this.getActiveNavMenuItemId === 'MACHINES'
      ) {
        if (isVolumeSubscription) {
          this.updateActiveNavMenuItemId('MACHINES');
          this.updateActiveContentType(MACHINES_TABLE_TYPE);
          this.handleNavMenuItemSelected({ id: 'MACHINES' });
        } else {
          this.updateActiveNavMenuItemId('BACKUPS');
          this.updateActiveContentType(BACKUP_GROUP_TYPE);
          this.handleNavMenuItemSelected({ id: 'BACKUPS' });
        }
      }

      this.fetchStorageSpace({
        subAccountId: subAccountId,
        volumeSubscription: isVolumeSubscription
      });
    },
    getPostLoginOpenNavMenuId(menuItem) {
      this.handleNavMenuItemSelected(menuItem);
    },
    // getFetchSubAccountArchivesStatus(status) {
    //   this.changeLoadingState(status);
    // },
    getPostLoginOpenArchiveId(archive) {
      const archiveId = this.searchArchiveId(archive);
      if (archiveId !== '') {
        if (archiveId.group !== undefined && archiveId.group !== '') {
          this.handleNavMenuItemSelected({ id: archiveId.group });
        }
        if (archiveId.options.passwordRequired) {
          this.passwordDialogBoxBox = archiveId;
          this.showPasswordDialogBox(true);
        } else {
          this.handleBoxSelected({
            boxId: archiveId.id
          });
        }
      }
    },
    getSearchEnabled(enabled) {
      if (!enabled) {
        this.isSearchInputVisible = false;
        this.searchInputValue = '';
      }
    }
  },
  methods: {
    ...mapActions({
      fetchNavMenuItems: FETCH_NAV_MENU_ITEMS,
      fetchStorageSpace: FETCH_STORAGE_SPACE,
      fetchContentItems: FETCH_CONTENT_ITEMS,
      fetchSubAccountItems: FETCH_SUB_ACCOUNT_ITEMS,
      searchContentItems: SEARCH_CONTENT_ITEMS,
      updateActiveContentType: UPDATE_ACTIVE_CONTENT_TYPE,
      updateActiveNavMenuItemId: UPDATE_ACTIVE_NAV_MENU_ITEM_ID,
      checkSubAccountExists: CHECK_SUB_ACCOUNT_EXISTS,
      fetchSubAccountArchives: FETCH_SUB_ACCOUNT_ARCHIVES,
      updateActiveVolumeSbSubAccountId: UPDATE_ACTIVE_VOLUME_SB_SUB_ACCOUNT_ID
    }),
    init() {
      this.checkSubAccountExists();
    },
    searchButtonClick() {
      this.isSearchInputVisible = true;

      this.$gtm.push({
        event: 'Acronis',
        eventCategory: 'WRE_backups',
        eventAction: 'Backup_search',
        eventLabel: ''
      });
    },
    helpButtonClick() {
      this.$gtm.push({
        event: 'Acronis',
        eventCategory: 'WRE_backups',
        eventAction: 'Help_open',
        eventLabel: ''
      });
      window.open(this.$t('wre.main-page.link.user-help'), '_blank');
    },
    getOSFamily() {
      const app = navigator.appVersion.toLowerCase();
      if (app.indexOf('win') !== -1) return 'Win';
      if (app.indexOf('mac') !== -1) return 'Mac';
      if (app.indexOf('x11') !== -1) return 'Unix';
      if (app.indexOf('linux') !== -1) return 'Linux';
      return 'Unknown';
    },
    handleBoxSelected(selectedItem) {
      this.updateActiveContentType(CONTENT_TABLE_TYPE);
      this.selectedBox = this.getSelectedBox(selectedItem.boxId);
      this.statusColumnHidden = !this.selectedBox.options.notarized;

      this.loadContent({
        archiveId: selectedItem.boxId,
        path: ''
      });

      const label = backupType(this.selectedBox.type, this.selectedBox.options.notarized).label;
      this.$gtm.push({
        event: 'Acronis',
        eventCategory: 'WRE_backups',
        eventAction: 'Backup_browsing',
        eventLabel: this.selectedBox.group === 'BACKUPS' ? label : ''
      });
    },
    handleMachineSelected(selectedItem) {
      this.updateActiveContentType(BACKUP_GROUP_TYPE);
      this.selectedBox = '';

      this.selectedMachine = {
        subAccountId: selectedItem.subAccountId,
        displayName: selectedItem.displayName
      };

      this.fetchSubAccountArchives({
        subAccountId: selectedItem.subAccountId
      });
    },
    handleBreadcrumbItemClick(selectedItem) {
      if (selectedItem.menuItem !== undefined) {
        this.handleNavMenuItemSelected(selectedItem.menuItem);
      } else if (selectedItem.subAccountId !== undefined) {
        this.handleMachineSelected({
          subAccountId: selectedItem.subAccountId,
          displayName: selectedItem.crumbTitle
        });
      } else if (selectedItem.isSearchFolder === true) {
        this.onSearchInputChange(selectedItem.crumbTitle);
      } else {
        this.loadContent(selectedItem);
      }
    },
    loadContent(selectedItem) {
      this.fetchContentItems(selectedItem);
    },
    getSelectedBox(boxId) {
      return this.getSubAccountArchives.filter(obj => obj.id === boxId)[0];
    },
    searchArchiveId(archive) {
      if (archive.id !== '') {
        const archiveId = this.getSubAccountArchives.filter(obj => obj.id === archive.id);
        if (archiveId.length > 0 && archiveId[0] !== 'undefined') {
          return archiveId[0];
        }
      }

      if (archive.baseName !== '') {
        const archiveId = this.getSubAccountArchives.filter(obj => obj.baseName === archive.baseName);
        if (archiveId.length > 0 && archiveId[0] !== 'undefined') {
          return archiveId[0];
        }
      }

      return '';
    },
    changeLoadingState(status) {
      if (status === PENDING_STATUS) {
        this.isFullScreenLoaderActive = true;
        setTimeout(() => {
          if (this.isFullScreenLoaderActive) {
            this.showLoading();
          }
        }, 300);
      } else {
        this.isFullScreenLoaderActive = false;
        this.hideLoading();
      }
    },
    showLoading() {
      this.fullScreenLoader = this.$loading({
        background: 'fixed-modal',
        color: 'fixed-white',
        size: '48',
        lock: true
      });
    },
    hideLoading() {
      if (this.fullScreenLoader !== '') {
        this.fullScreenLoader.close();
      }
    },
    showPasswordDialogBox(show) {
      this.passwordDialogBoxVisible = show;
    },
    handlePasswordSubmit(payload) {
      this.showPasswordDialogBox(false);
      this.handleBoxSelected({
        boxId: this.passwordDialogBoxBox.id
      });
    },
    handlePasswordClose() {
      this.showPasswordDialogBox(false);
    },
    handleNavMenuItemSelected(menuItem) {
      this.updateActiveVolumeSbSubAccountId(0);
      this.selectedMachine = { subAccountId: 0, displayName: '' };
      this.updateActiveNavMenuItemId(menuItem.id);
      this.activeMenuItem = {
        id: menuItem.id,
        name: this.getHeaderNameByNavMenuItemId(menuItem.id)
      };

      if (menuItem.id === 'shared') {
        this.updateActiveContentType(SHARED_CONTENT_TABLE_TYPE);
        return;
      }
      if (menuItem.id === 'account') {
        this.updateActiveContentType(ACCOUNT_PAGE_CONTENT_TYPE);
        this.fetchStorageSpace({
          subAccountId: this.getActiveSubAccountId,
          volumeSubscription: (this.getActiveSubAccountId < 0)
        });
        return;
      }

      if (menuItem.id === 'MACHINES') {
        this.updateActiveContentType(MACHINES_TABLE_TYPE);
        this.selectedBox = '';

        if (this.getVolumeSbMachines.length <= 0) {
          this.fetchSubAccountItems();
        }
        return;
      }
      if (menuItem.id === 'BACKUPS') {
        this.updateActiveContentType(BACKUP_GROUP_TYPE);
        this.selectedBox = '';

        this.fetchSubAccountArchives({
          subAccountId: this.getActiveSubAccountId
        });
        return;
      }

      console.log('Undefined menu id', menuItem.id);
    },
    getHeaderNameByNavMenuItemId(menuItemId) {
      if (menuItemId === 'BACKUPS') {
        return this.$t('wre-dashboard.nav-menu.item.backups');
      }
      if (menuItemId === 'MACHINES') {
        return this.$t('wre-dashboard.nav-menu.item.machines');
      }
      if (menuItemId === 'shared') {
        return this.$t('wre-dashboard.nav-menu.item.shared');
      }
      if (menuItemId === 'account') {
        return this.$t('wre-dashboard.nav-menu.item.account');
      }
      return 'Undefined Menu Item';
    },
    onSearchInputIconClick() {
      this.onSearchInputChange(this.searchInputValue);
    },
    onSearchInputChange(value) {
      if (value !== '') {
        this.searchContentItems({
          archiveId: this.selectedBox.id,
          volumePath: this.getActiveVolumePath,
          path: this.getActiveFolderItem.path,
          searchText: value
        });
      }
    },
    onShowSubAccountsTable() {
      this.isSubAccountsTableVisible = true;
    },
    handSubAccountsTableClose() {
      this.isSubAccountsTableVisible = false;
    }
  },
  async created() {
    await this.init();
  }
};
</script>
